"use client";

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useStore } from "@/lib/store";
import { createClient } from "@/lib/supabase/client";
import { clearPersonalFocus, confirmPersonalFocus, loadPersonalFocus } from "@/lib/data/personalFocus";
import { emptyFocusFilters, normalizeFocusFilters, personalFocusFilterOptions, selectPersonalFocus, type FocusFilters, type FocusPeriod } from "@/lib/personalFocus";
import { humanOwnerId } from "@/lib/taskOwnership";
import { PersonalFocusController } from "@/lib/personalFocusController";
import { loadPersonalFocusSourceRelations } from "@/lib/data/personalFocusSources";
import type { AuthorizedFocusSourceRelation } from "@/lib/personalFocusEvidence";
import { subscribeTaskWrites, waitForTaskWrites } from "@/lib/taskWriteLifecycle";

const adapter = {
  load: async (workspaceId: string, userId: string, signal: AbortSignal) => {
    await waitForTaskWrites(workspaceId, userId, signal);
    return loadPersonalFocus(workspaceId, userId, signal);
  },
  confirm: confirmPersonalFocus, clear: clearPersonalFocus,
};

export function usePersonalFocus(workspaceId: string, userId: string) {
  const issues = useStore((state) => state.issues);
  // A new key produces an empty snapshot in this render, before effect cleanup.
  const controller = useMemo(() => new PersonalFocusController(workspaceId, userId, adapter), [workspaceId, userId]);
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
  const [projection, setProjection] = useState(() => ({
    owner: controller, snapshot, period: 30 as FocusPeriod, filters: emptyFocusFilters(), now: Date.now(),
  }));
  let currentProjection = projection;
  if (projection.owner !== controller || projection.snapshot !== snapshot) {
    // Scope changes reset in this render, including A → B → A. Sampling each
    // snapshot also ages a slow response before it becomes visible.
    currentProjection = {
      owner: controller, snapshot,
      period: projection.owner === controller ? projection.period : 30,
      filters: projection.owner === controller ? projection.filters : emptyFocusFilters(),
      now: Date.now(),
    };
    setProjection(currentProjection);
  }
  const { period, filters, now } = currentProjection;
  const setPeriod = useCallback((next: FocusPeriod) => {
    setProjection((current) => current.owner === controller && current.period !== next
      ? { ...current, period: next, now: Date.now() } : current);
  }, [controller]);
  const setFilters = useCallback((next: FocusFilters) => {
    const normalized = normalizeFocusFilters(next);
    setProjection((current) => current.owner === controller && JSON.stringify(current.filters) !== JSON.stringify(normalized)
      ? { ...current, filters: normalized, now: Date.now() } : current);
  }, [controller]);
  const advanceClock = useCallback(() => {
    const now = Date.now();
    setProjection((current) => current.owner === controller && current.now !== now
      ? { ...current, now } : current);
  }, [controller]);
  const refreshNow = useCallback(() => {
    advanceClock();
    void controller.refresh();
  }, [controller, advanceClock]);

  useEffect(() => {
    controller.activate();
    if (!workspaceId || !userId) return () => controller.dispose();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const refresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(refreshNow, 250);
    };
    controller.observeInventory(useStore.getState().issues);
    refreshNow();
    const unsubscribe = useStore.subscribe((state, previous) => {
      if (state.activeWorkspaceId !== workspaceId || state.currentUserId !== userId) {
        controller.dispose();
        return;
      }
      if (state.issues !== previous.issues && controller.observeInventory(state.issues)) {
        controller.invalidateRead();
        refresh();
      }
    });
    const unsubscribeWrites = subscribeTaskWrites(workspaceId, userId, (issueId) => {
      controller.taskWriteSettled(issueId);
      refresh();
    });
    const client = createClient();
    const channel = client.channel(`personal-focus:${workspaceId}:${userId}`);
    for (const table of ["issues", "personal_task_focus", "projects", "issue_labels", "labels"]) {
      channel.on("postgres_changes", { event: "*", schema: "public", table,
        ...(["issue_labels", "labels"].includes(table) ? {} : { filter: `workspace_id=eq.${workspaceId}` }) }, () => {
        // Invalidate pending pagination immediately; debounce only the fresh read.
        controller.invalidateRead();
        refresh();
      });
    }
    channel.subscribe();
    const { data: { subscription } } = client.auth.onAuthStateChange((_event, session) => {
      if (session?.user.id !== userId) controller.dispose();
    });
    const regainVisibility = () => { if (!document.hidden) refreshNow(); };
    const clockTimer = setInterval(() => { if (!document.hidden) advanceClock(); }, 60_000);
    window.addEventListener("focus", regainVisibility);
    document.addEventListener("visibilitychange", regainVisibility);
    return () => {
      if (timer) clearTimeout(timer);
      clearInterval(clockTimer);
      unsubscribe();
      unsubscribeWrites();
      subscription.unsubscribe();
      window.removeEventListener("focus", regainVisibility);
      document.removeEventListener("visibilitychange", regainVisibility);
      void client.removeChannel(channel);
      controller.dispose();
    };
  }, [controller, userId, workspaceId, refreshNow, advanceClock]);

  const model = useMemo(() => snapshot.loaded ? selectPersonalFocus({
    workspaceId, userId, issues: snapshot.tasks, projects: snapshot.projects, preference: snapshot.preference, period, filters, now,
    dependencyTasks: [...snapshot.tasks, ...issues.filter((task) => task.workspaceId === workspaceId && !snapshot.tasks.some((candidate) => candidate.id === task.id))],
  }) : null, [snapshot, workspaceId, userId, issues, period, filters, now]);
  const filterOptions = useMemo(() => personalFocusFilterOptions(
    snapshot.tasks.filter((task) => humanOwnerId(task) === userId && !["done", "canceled"].includes(task.status)),
    snapshot.projects, snapshot.labels, workspaceId,
  ), [snapshot, workspaceId, userId]);

  const sourceKey = model?.primary ? `${workspaceId}:${userId}:${model.primary.id}` : null;
  const [sources, setSources] = useState<{ key: string | null; owner: PersonalFocusController | null; revision: number; relations: AuthorizedFocusSourceRelation[]; error: boolean }>({ key: null, owner: null, revision: -1, relations: [], error: false });
  const [sourceRetry, setSourceRetry] = useState(0);
  useEffect(() => {
    const issueId = model?.primary?.id;
    if (!sourceKey || !issueId) return;
    const abort = new AbortController();
    void loadPersonalFocusSourceRelations(workspaceId, userId, issueId, abort.signal).then((relations) => {
      if (!abort.signal.aborted) setSources({ key: sourceKey, owner: controller, revision: snapshot.revision, relations, error: false });
    }).catch(() => {
      if (!abort.signal.aborted) setSources({ key: sourceKey, owner: controller, revision: snapshot.revision, relations: [], error: true });
    });
    return () => abort.abort();
  }, [sourceKey, controller, workspaceId, userId, model?.primary?.id, snapshot.revision, sourceRetry]);

  return {
    ...snapshot, model, period, setPeriod, filters, setFilters, filterOptions,
    sourceRelations: sources.key === sourceKey && sources.owner === controller && sources.revision === snapshot.revision ? sources.relations : [],
    sourceError: sources.key === sourceKey && sources.owner === controller && sources.revision === snapshot.revision && sources.error,
    confirm: (id: string) => { void controller.confirm(id); },
    clear: () => { void controller.clear(); },
    retry: () => { setSourceRetry((value) => value + 1); refreshNow(); },
  };
}
